<script setup lang="ts">
import VideoCallPanel from "~/components/video-call/VideoCallPanel.vue";
import VideoCallGuestWaitingWidget from "~/components/video-call/VideoCallGuestWaitingWidget.vue";
import VideoCallMediaSettings from "~/components/video-call/VideoCallMediaSettings.vue";
import VideoCallVideo from "~/components/video-call/VideoCallVideo.vue";
import { useVideoCallLauncher } from "~/composables/useVideoCallLauncher";
import { useStudentAccess } from "~/composables/useStudentAccess";
import { useAuthStore } from "~/stores/auth";
import { useVideoCallsStore } from "~/stores/videoCalls";
import { VIDEO_CALL_IDENTITY_WILL_CHANGE_EVENT } from "~/utils/videoCalls/identity";
import { isVideoCallRoutePath } from "~/utils/videoCalls/privacy";

const route = useRoute();
const authStore = useAuthStore();
const store = useVideoCallsStore();
const { canAccessLearning } = useStudentAccess();

const isVideoCallPage = computed(() => isVideoCallRoutePath(route.path));
const isAuthenticatedTeacher = computed(
  () => authStore.profileLoaded && authStore.user.permissions.isTeacher,
);
const isAuthenticatedStudentOnly = computed(
  () =>
    authStore.profileLoaded &&
    authStore.user.permissions.isStudent &&
    !authStore.user.permissions.isTeacher,
);
const { canStartVideoCall, placement } = useVideoCallLauncher();
const shouldShowLauncher = computed(() => placement.value === "floating");
const shouldShowHostSession = computed(
  () =>
    !isVideoCallPage.value &&
    store.role === "host" &&
    ["preparing", "preview", "active"].includes(store.phase),
);
const shouldShowGuestWaiting = computed(
  () =>
    !isVideoCallPage.value &&
    isAuthenticatedStudentOnly.value &&
    store.role === "guest" &&
    store.phase === "waiting" &&
    store.canContinueGuestInBackground,
);
const shouldShowGuestActive = computed(
  () =>
    !isVideoCallPage.value &&
    isAuthenticatedStudentOnly.value &&
    store.role === "guest" &&
    store.phase === "active" &&
    store.canContinueGuestInBackground,
);
const shouldShowActiveSession = computed(
  () =>
    (shouldShowHostSession.value && store.phase === "active") ||
    shouldShowGuestActive.value,
);
const localName = computed(
  () =>
    `${authStore.user.firstName} ${authStore.user.lastName}`.trim() ||
    "Professor",
);
const guestName = computed(
  () =>
    store.connection?.participant.name ||
    store.joinRequest?.name ||
    `${authStore.user.firstName} ${authStore.user.lastName}`.trim() ||
    "Você",
);
const isRestoredHostCall = computed(() => store.call?.status === "active");

const shellElement = ref<HTMLElement | null>(null);
// Below this width the panel already fills the screen, so there is nowhere to
// drag it to.
const compactViewport = ref(false);
let viewportQuery: MediaQueryList | null = null;

const syncCompactViewport = () => {
  compactViewport.value = viewportQuery?.matches === true;
};

const panelDraggable = computed(() => {
  if (!shouldShowActiveSession.value || !store.panelDraggable) return false;
  // On a narrow screen the medium panel already fills it, so there is nowhere
  // to drag it to. Focus mode stays a small frame and remains movable.
  return !compactViewport.value || store.panelSize === "small";
});

const panelDrag = useVideoCallPanelDrag({
  element: shellElement,
  enabled: panelDraggable,
});

// A size change moves the panel's edges, so the offset is re-checked against
// the viewport instead of being thrown away.
watch(() => store.panelSize, panelDrag.reclamp);

const returnToGuestCall = async () => {
  await navigateTo("/video-call");
};

const onIdentityWillChange = () => {
  // eslint-disable-next-line no-void
  void store.handleIdentityWillChange().catch(() => undefined);
};

watch(
  () =>
    [
      isAuthenticatedTeacher.value,
      isAuthenticatedStudentOnly.value,
      authStore.user.id,
      isVideoCallPage.value,
      canAccessLearning.value,
    ] as const,
  ([isTeacher, isStudentOnly, , onVideoCallPage, canLearn]) => {
    if (isTeacher) {
      store.initializeHost();
      return;
    }

    if (isStudentOnly && !canLearn) {
      if (store.role === "guest") store.leaveGuestCall().catch(() => undefined);
      return;
    }

    if (isStudentOnly && !onVideoCallPage) {
      // eslint-disable-next-line no-void
      void store.restoreGuestBackgroundSession().catch(() => undefined);
    }
  },
  { immediate: true },
);

watch(
  () =>
    [
      store.phase,
      store.teacherLinkStatus,
      isAuthenticatedStudentOnly.value,
      isVideoCallPage.value,
    ] as const,
  ([phase, teacherLinkStatus, isStudentOnly, onVideoCallPage]) => {
    if (
      !onVideoCallPage &&
      isStudentOnly &&
      phase === "active" &&
      teacherLinkStatus === "waiting_for_admission"
    ) {
      // Account linking is best-effort and must not interrupt the call.
      // eslint-disable-next-line no-void
      void store.linkGuestAccount().catch(() => undefined);
    }
  },
);

onMounted(() => {
  window.addEventListener(
    VIDEO_CALL_IDENTITY_WILL_CHANGE_EVENT,
    onIdentityWillChange,
  );
  viewportQuery = window.matchMedia("(max-width: 600px)");
  syncCompactViewport();
  viewportQuery.addEventListener("change", syncCompactViewport);
});

onBeforeUnmount(() => {
  window.removeEventListener(
    VIDEO_CALL_IDENTITY_WILL_CHANGE_EVENT,
    onIdentityWillChange,
  );
  viewportQuery?.removeEventListener("change", syncCompactViewport);
  viewportQuery = null;
});
</script>

<template>
  <aside
    v-if="!isVideoCallPage"
    ref="shellElement"
    class="video-call-global"
    :class="{
      'video-call-global--full-width':
        shouldShowActiveSession && store.panelSize !== 'small',
    }"
    :style="panelDrag.style.value"
    data-sentry-block
  >
    <button
      v-if="shouldShowLauncher"
      type="button"
      class="video-call-launcher"
      data-testid="video-call-launcher"
      @click="store.prepareHostCall"
    >
      <i class="mdi mdi-video-outline" />
      <span>Abrir sala de vídeo</span>
    </button>

    <section
      v-else-if="
        shouldShowHostSession &&
        (store.phase === 'preparing' || store.phase === 'preview')
      "
      class="video-call-prejoin"
      :aria-busy="store.phase === 'preparing'"
      data-testid="video-call-host-preview"
    >
      <header>
        <div>
          <span>Chamada de vídeo</span>
          <h2>Confira sua câmera e seu microfone</h2>
        </div>
        <button
          type="button"
          :aria-label="
            isRestoredHostCall
              ? 'Encerrar chamada recuperada'
              : 'Fechar preparação da chamada'
          "
          :disabled="store.loading"
          @click="store.cancelHostPreparation"
        >
          <i class="mdi mdi-close" />
        </button>
      </header>

      <div
        v-if="store.phase === 'preparing'"
        class="video-call-prejoin__loading"
        role="status"
        aria-live="polite"
        data-testid="video-call-media-loading"
      >
        <span class="video-call-prejoin__loading-icon" aria-hidden="true">
          <i class="mdi mdi-loading mdi-spin" />
        </span>
        <strong>Preparando câmera e microfone</strong>
        <p>
          Autorize o acesso no navegador, se solicitado. Isso pode levar alguns
          segundos.
        </p>
      </div>

      <template v-else>
        <VideoCallVideo
          :stream="store.getLocalStream()"
          :name="localName"
          local
          :camera-enabled="store.cameraEnabled"
          :microphone-enabled="store.microphoneEnabled"
        />

        <p
          v-if="store.mediaError"
          class="video-call-prejoin__error"
          role="alert"
        >
          {{ store.mediaError }} Você ainda pode abrir a sala e tentar novamente
          pelos controles.
        </p>
        <p
          v-if="store.errorMessage"
          class="video-call-prejoin__error"
          role="alert"
        >
          {{ store.errorMessage }}
        </p>

        <div class="video-call-prejoin__controls">
          <button
            type="button"
            :aria-label="
              store.microphoneEnabled ? 'Desligar microfone' : 'Ligar microfone'
            "
            @click="store.toggleMicrophone"
          >
            <i
              class="mdi"
              :class="
                store.microphoneEnabled
                  ? 'mdi-microphone'
                  : 'mdi-microphone-off'
              "
            />
          </button>
          <button
            type="button"
            :aria-label="
              store.cameraEnabled ? 'Desligar câmera' : 'Ligar câmera'
            "
            @click="store.toggleCamera"
          >
            <i
              class="mdi"
              :class="
                store.cameraEnabled ? 'mdi-video' : 'mdi-video-off-outline'
              "
            />
          </button>
          <VideoCallMediaSettings overlay />
        </div>

        <footer>
          <button
            type="button"
            class="secondary-button"
            :disabled="store.loading"
            @click="store.cancelHostPreparation"
          >
            {{ isRestoredHostCall ? "Encerrar chamada" : "Cancelar" }}
          </button>
          <button
            type="button"
            class="primary-button"
            :disabled="store.loading"
            @click="store.startHostCall"
          >
            <i
              class="mdi"
              :class="store.loading ? 'mdi-loading mdi-spin' : 'mdi-video-plus'"
            />
            {{
              store.loading
                ? isRestoredHostCall
                  ? "Retomando..."
                  : "Abrindo..."
                : isRestoredHostCall
                  ? "Retomar chamada"
                  : "Abrir sala de vídeo"
            }}
          </button>
        </footer>
      </template>
    </section>

    <VideoCallGuestWaitingWidget
      v-else-if="shouldShowGuestWaiting"
      :guest-name="guestName"
      :stream="store.getLocalStream()"
      :host-name="store.preview?.host_name"
      :camera-enabled="store.cameraEnabled"
      :microphone-enabled="store.microphoneEnabled"
      :error-message="store.errorMessage"
      @toggle-microphone="store.toggleMicrophone"
      @toggle-camera="store.toggleCamera"
      @return-to-call="returnToGuestCall"
      @cancel="store.leaveGuestCall"
    />

    <VideoCallPanel
      v-else-if="shouldShowActiveSession"
      :draggable="panelDraggable"
      :dragging="panelDrag.dragging.value"
      @drag-start="panelDrag.onPointerDown"
      @drag-keydown="panelDrag.onKeydown"
    />

    <div
      v-else-if="
        canStartVideoCall &&
        (store.phase === 'ended' || store.phase === 'error') &&
        store.role !== 'guest'
      "
      class="video-call-ended"
    >
      <i class="mdi mdi-phone-check-outline" />
      <span :role="store.errorMessage ? 'alert' : 'status'">
        <strong>{{ store.errorMessage || "Chamada encerrada" }}</strong>
        Clique para fechar
      </span>
      <button
        type="button"
        aria-label="Fechar aviso da chamada"
        @click="store.dismissEndedState"
      >
        <i class="mdi mdi-close" />
      </button>
    </div>
  </aside>
</template>

<style scoped lang="scss">
.video-call-global {
  position: fixed;
  z-index: 2400;
  bottom: max(16px, env(safe-area-inset-bottom));
  left: max(16px, env(safe-area-inset-left));
  max-width: calc(100vw - max(16px, env(safe-area-inset-left)) - 16px);
  font-family: Inter, Roboto, sans-serif;
}

.video-call-launcher,
.video-call-ended {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-height: 48px;
  padding: 10px 15px;
  border: 1px solid rgba(49, 86, 255, 0.16);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 14px 34px rgba(26, 43, 82, 0.2);
  color: #26334d;
  font: inherit;
  font-size: 0.83rem;
  font-weight: 700;
  cursor: pointer;

  > i:first-child {
    color: #3156ff;
    font-size: 1.25rem;
  }
}

.video-call-ended {
  cursor: default;
  text-align: left;

  span {
    display: grid;
    font-size: 0.7rem;
    font-weight: 500;
  }

  strong {
    color: #26334d;
    font-size: 0.8rem;
  }

  > button {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    margin-left: 4px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #798399;
    cursor: pointer;
  }
}

.video-call-prejoin {
  width: min(380px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  padding: 14px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid #dbe1ef;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 22px 60px rgba(17, 30, 55, 0.24);
  color: #26334d;

  > header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;

    span {
      color: #6f7b91;
      font-size: 0.68rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }

    h2 {
      margin: 3px 0 0;
      color: #17233c;
      font-size: 1rem;
      line-height: 1.3;
    }

    button {
      display: grid;
      place-items: center;
      flex: 0 0 34px;
      width: 34px;
      height: 34px;
      border: 0;
      border-radius: 9px;
      background: #f0f3f8;
      color: #4f5d75;
      cursor: pointer;
    }
  }

  > footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }
}

.video-call-prejoin__controls {
  display: flex;
  justify-content: center;
  gap: 9px;
  margin-top: -54px;
  padding-bottom: 14px;
  position: relative;
  z-index: 2;

  button {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border: 1px solid rgba(255, 255, 255, 0.24);
    border-radius: 12px;
    background: rgba(19, 28, 48, 0.82);
    color: #fff;
    cursor: pointer;
  }
}

.video-call-prejoin__loading {
  display: grid;
  min-height: 236px;
  place-content: center;
  justify-items: center;
  gap: 8px;
  padding: 28px 22px;
  border-radius: 14px;
  background: #f5f7fb;
  text-align: center;

  strong {
    color: #17233c;
    font-size: 0.9rem;
  }

  p {
    max-width: 280px;
    margin: 0;
    color: #69758b;
    font-size: 0.76rem;
    line-height: 1.45;
  }
}

.video-call-prejoin__loading-icon {
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  margin-bottom: 4px;
  border-radius: 50%;
  background: rgba(49, 86, 255, 0.1);
  color: #3156ff;
  font-size: 1.55rem;
}

.video-call-prejoin__error {
  margin: 10px 0 0;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fff0f1;
  color: #a51f34;
  font-size: 0.74rem;
  line-height: 1.35;
}

.primary-button,
.secondary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 38px;
  padding: 8px 12px;
  border-radius: 10px;
  font: inherit;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.primary-button {
  border: 1px solid #3156ff;
  background: #3156ff;
  color: #fff;
}

.secondary-button {
  border: 1px solid #ccd4e2;
  background: #fff;
  color: #4f5d75;
}

@media (max-width: 600px) {
  .video-call-global--full-width {
    inset: 0;
    max-width: none;
  }
}
</style>
